import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { getAuth, clearAuth, hasPermission } from '../utils/auth';
import { t } from '../utils/i18n';
import { useLanguage } from '../contexts/LanguageContext';
import { notificationsAPI } from '../services/api';

function Layout({ children }) {
    const location = useLocation();
    const navigate = useNavigate();
    const { user, permissions } = getAuth();
    const { currentLang, setLanguage, languages } = useLanguage();
    const [, forceUpdate] = useState(0);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const [showNotifications, setShowNotifications] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);

    useEffect(() => {
        const handleLanguageChange = () => {
            forceUpdate(prev => prev + 1);
        };
        window.addEventListener('languageChanged', handleLanguageChange);
        return () => window.removeEventListener('languageChanged', handleLanguageChange);
    }, []);

    useEffect(() => {
        if (user) {
            loadNotifications();
            // Poll for new notifications every 30 seconds
            const interval = setInterval(loadNotifications, 30000);
            return () => clearInterval(interval);
        }
    }, [user]);

    useEffect(() => {
        // Close notifications when clicking outside
        const handleClickOutside = (event) => {
            if (showNotifications && !event.target.closest('.notifications-container')) {
                setShowNotifications(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showNotifications]);

    const loadNotifications = async () => {
        try {
            console.log('📬 Loading notifications...');
            const response = await notificationsAPI.getAll();
            console.log('📬 Notifications response:', response.data);
            if (response.data.success) {
                const notifs = response.data.notifications || [];
                console.log('📬 Setting notifications:', notifs.length);
                setNotifications(notifs);
                setUnreadCount(notifs.filter(n => !n.isRead).length);
            } else {
                console.error('📬 Notifications API returned success: false');
            }
        } catch (error) {
            console.error('❌ Error loading notifications:', error);
            console.error('❌ Error response:', error.response);
            console.error('❌ Error message:', error.message);
        }
    };

    const handleMarkAsRead = async (notificationId) => {
        try {
            await notificationsAPI.markAsRead(notificationId);
            loadNotifications();
        } catch (error) {
            console.error('Error marking notification as read:', error);
        }
    };

    const handleMarkAllAsRead = async () => {
        try {
            await notificationsAPI.markAllAsRead();
            loadNotifications();
        } catch (error) {
            console.error('Error marking all as read:', error);
        }
    };

    const handleLogout = () => {
        clearAuth();
        // Use window.location to force a full page reload and clear React state
        window.location.href = '/login';
    };

    const getInitials = (name) => {
        if (!name) return 'U';
        return name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);
    };

    const translateRole = (role) => {
        return t(role) || role;
    };

    return (
        <div className="dashboard-container">
            <aside className={`sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
                <div className="sidebar-header">
                    <button 
                        className="sidebar-toggle"
                        onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                        title={sidebarCollapsed ? t('expand') : t('collapse')}
                    >
                        <i className={`fas fa-chevron-${sidebarCollapsed ? 'right' : 'left'}`}></i>
                    </button>
                    {!sidebarCollapsed && (
                        <>
                            <div className="sidebar-logo-container">
                                <img 
                                    src="/atom.png" 
                                    alt="Անտրոհուս կրթական հիմնադրամ" 
                                    className="sidebar-logo"
                                />
                            </div>
                            <h2>{t('appTitle')}</h2>
                            <small>{t('appSubtitle')}</small>
                        </>
                    )}
                </div>

                {!sidebarCollapsed && (
                    <>
                        {/* Language Selector in Sidebar */}
                        <div style={{ 
                            padding: '16px', 
                            borderBottom: '1px solid var(--border-color)',
                            background: 'var(--bg-secondary)'
                        }}>
                            <select
                                className="form-select"
                                value={currentLang}
                                onChange={(e) => setLanguage(e.target.value)}
                                style={{
                                    width: '100%',
                                    fontSize: '13px',
                                    padding: '8px 12px'
                                }}
                            >
                                {languages.map(lang => (
                                    <option key={lang.code} value={lang.code}>
                                        {lang.native}
                                    </option>
                                ))}
                            </select>
                        </div>
                        
                        <nav className="sidebar-menu">
                            <Link 
                                to="/dashboard" 
                                className={`menu-item ${location.pathname === '/dashboard' ? 'active' : ''}`}
                            >
                                <i className="fas fa-home menu-icon"></i>
                                <span>{t('dashboard')}</span>
                            </Link>
                            
                            <Link 
                                to="/projects" 
                                className={`menu-item ${location.pathname === '/projects' ? 'active' : ''}`}
                            >
                                <i className="fas fa-project-diagram menu-icon"></i>
                                <span>{t('projects')}</span>
                            </Link>

                            <Link 
                                to="/dictionary" 
                                className={`menu-item ${location.pathname === '/dictionary' ? 'active' : ''}`}
                            >
                                <i className="fas fa-book menu-icon"></i>
                                <span>{t('dictionary')}</span>
                            </Link>
                            
                            {hasPermission('canManageUsers') && (
                                <Link 
                                    to="/admin" 
                                    className={`menu-item ${location.pathname === '/admin' ? 'active' : ''}`}
                                >
                                    <i className="fas fa-users menu-icon"></i>
                                    <span>{t('admin')}</span>
                                </Link>
                            )}
                        </nav>
                        
                        <div className="sidebar-footer">
                            <div className="user-info">
                                <div className="user-avatar">
                                    {getInitials(user?.full_name)}
                                </div>
                                <div className="user-details">
                                    <div className="user-name">{user?.full_name || user?.username}</div>
                                    <div className="user-role">{translateRole(user?.role)}</div>
                                </div>
                            </div>
                            <button 
                                onClick={handleLogout}
                                className="btn btn-outline btn-sm"
                                style={{ marginTop: '12px', width: '100%' }}
                            >
                                <i className="fas fa-sign-out-alt"></i>
                                <span>{t('logout')}</span>
                            </button>
                        </div>
                    </>
                )}
            </aside>
            
            <main className="main-content">
                <div className="header-bar" style={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 100,
                    background: 'var(--bg-color)',
                    borderBottom: '1px solid var(--border-color)',
                    padding: '12px 24px',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    gap: '16px'
                }}>
                    <div className="notifications-container" style={{ position: 'relative' }}>
                        <button
                            onClick={() => setShowNotifications(!showNotifications)}
                            className="btn btn-outline"
                            style={{ position: 'relative', padding: '8px 12px' }}
                        >
                            <i className="fas fa-bell"></i>
                            {unreadCount > 0 && (
                                <span style={{
                                    position: 'absolute',
                                    top: '-4px',
                                    right: '-4px',
                                    background: 'var(--brand-primary)',
                                    color: 'white',
                                    borderRadius: '50%',
                                    width: '20px',
                                    height: '20px',
                                    fontSize: '11px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontWeight: 'bold'
                                }}>
                                    {unreadCount > 9 ? '9+' : unreadCount}
                                </span>
                            )}
                        </button>
                        {showNotifications && (
                            <div style={{
                                position: 'absolute',
                                top: '100%',
                                right: 0,
                                marginTop: '8px',
                                background: 'white',
                                border: '1px solid var(--border-color)',
                                borderRadius: '8px',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                width: '350px',
                                maxHeight: '400px',
                                overflowY: 'auto',
                                zIndex: 1000
                            }}>
                                <div style={{
                                    padding: '12px 16px',
                                    borderBottom: '1px solid var(--border-color)',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center'
                                }}>
                                    <strong>{t('notifications')}</strong>
                                    {unreadCount > 0 && (
                                        <button
                                            onClick={handleMarkAllAsRead}
                                            className="btn btn-sm btn-outline"
                                            style={{ fontSize: '12px', padding: '4px 8px' }}
                                        >
                                            {t('markAllRead')}
                                        </button>
                                    )}
                                </div>
                                {notifications.length === 0 ? (
                                    <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-secondary)' }}>
                                        {t('noNotifications')}
                                    </div>
                                ) : (
                                    notifications.map(notif => {
                                        // Detect language from notification content
                                        const detectLanguage = (text) => {
                                            if (!text) return 'hy';
                                            // Check if text contains Armenian characters
                                            const armenianPattern = /[Ա-Ֆա-ֆ]/;
                                            if (armenianPattern.test(text)) {
                                                return 'hy';
                                            }
                                            // Check if text contains English characters
                                            const englishPattern = /[A-Za-z]/;
                                            if (englishPattern.test(text)) {
                                                return 'en';
                                            }
                                            return 'hy'; // Default to Armenian
                                        };
                                        
                                        const notifLang = detectLanguage(notif.title + ' ' + notif.message);
                                        const displayLang = notifLang === 'hy' ? 'hy' : 'en';
                                        
                                        return (
                                            <div
                                                key={notif.id}
                                                onClick={() => {
                                                    if (!notif.isRead) handleMarkAsRead(notif.id);
                                                }}
                                                style={{
                                                    padding: '12px 16px',
                                                    borderBottom: '1px solid var(--border-color)',
                                                    cursor: notif.isRead ? 'default' : 'pointer',
                                                    background: notif.isRead ? 'white' : 'var(--bg-secondary)',
                                                    transition: 'background 0.2s'
                                                }}
                                                onMouseEnter={(e) => {
                                                    if (!notif.isRead) {
                                                        e.currentTarget.style.background = 'var(--bg-secondary)';
                                                    }
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.background = notif.isRead ? 'white' : 'var(--bg-secondary)';
                                                }}
                                            >
                                                <div style={{ 
                                                    fontWeight: notif.isRead ? 'normal' : 'bold', 
                                                    marginBottom: '4px',
                                                    fontFamily: displayLang === 'hy' ? "'Arian AMU', 'Noto Sans Armenian', sans-serif" : 'inherit'
                                                }}>
                                                    {notif.title}
                                                </div>
                                                <div style={{ 
                                                    fontSize: '13px', 
                                                    color: 'var(--text-secondary)',
                                                    fontFamily: displayLang === 'hy' ? "'Arian AMU', 'Noto Sans Armenian', sans-serif" : 'inherit'
                                                }}>
                                                    {notif.message}
                                                </div>
                                                {notif.projectName && (
                                                    <div style={{ 
                                                        fontSize: '12px', 
                                                        color: 'var(--text-secondary)', 
                                                        marginTop: '4px',
                                                        fontFamily: displayLang === 'hy' ? "'Arian AMU', 'Noto Sans Armenian', sans-serif" : 'inherit'
                                                    }}>
                                                        {t('project')}: {notif.projectName}
                                                    </div>
                                                )}
                                                <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '4px' }}>
                                                    {new Date(notif.createdAt).toLocaleString(displayLang === 'hy' ? 'hy-AM' : 'en-US')}
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        )}
                    </div>
                </div>
                {children}
            </main>
        </div>
    );
}

export default Layout;

